import type { Color, GameState, Move, PieceState, Square } from '@ccc/shared-types';
import type { RuleModifier } from '@ccc/rule-engine';
import {
  ORTHO_OFFSETS,
  DIAG_OFFSETS,
  emptyAt,
  enemyAt,
  fileIndex,
  fileOf,
  friendlyAt,
  isAdjacent,
  makeMove,
  offsetSquare,
  pieceAt,
  piecesOfColor,
  rankOf,
  vaultedRay,
} from './helpers.js';

export function generalMomentumCheck(color: Color): RuleModifier {
  /**
   * "Check" in our world = the opposing king is now reachable by one of our pieces.
   * (We removed the check-must-be-defended rule, but the concept exists for hints.)
   * If the just-played move leaves the enemy king attacked, grant one bonus move.
   */
  return {
    id: 'general-momentum-check',
    ownerSide: color,
    conflictTags: ['turn.bonus'],
    priority: 40,
    hooks: {
      turnBonusGranted(move, state) {
        if (move.bonusOf !== undefined) return null;
        const mover = state.pieces[move.pieceId];
        if (!mover || mover.color !== color) return null;
        const enemy = color === 'white' ? 'black' : 'white';
        const king = piecesOfColor(state, enemy).find((p) => p.type === 'king');
        if (!king) return null;
        // Is the enemy king attacked? Conservative attack check — same logic the engine uses for legality.
        if (!squareAttackedBy(state, king.square, color)) return null;
        return {
          side: color,
          kind: 'momentum_check',
          canCapture: false,
          restrictToPieceIds: piecesOfColor(state, color).filter((p) => p.id !== mover.id).map((p) => p.id),
        };
      },
    },
  };
}

export function generalVaultingArmy(color: Color): RuleModifier {
  return {
    id: 'general-vaulting-army',
    ownerSide: color,
    conflictTags: ['movement.all'],
    priority: 45,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color) return [];
        const offsets = piece.type === 'bishop' ? DIAG_OFFSETS : piece.type === 'rook' ? ORTHO_OFFSETS : piece.type === 'queen' ? [...ORTHO_OFFSETS, ...DIAG_OFFSETS] : [];
        if (offsets.length === 0) return [];
        return vaultedRay(piece.square, state, color, offsets).map((to) =>
          ({ ...makeMove(piece, to, state), ruleTriggers: ['vault'], capturedPieceId: undefined as any }),
        ).filter((m) => !pieceAt(state, m.to) || (pieceAt(state, m.to)!.color !== color && false));
        // Above filter drops captures: vaulting cannot capture on the same turn.
      },
    },
  };
}

export function generalRetaliatoryStrike(color: Color): RuleModifier {
  return {
    id: 'general-retaliatory-strike',
    ownerSide: color,
    conflictTags: ['turn.bonus'],
    priority: 40,
    hooks: {
      reactToCapture(captured, _attacker, _state) {
        if (captured.color !== color) return null;
        return { bonusFor: color, note: 'retaliatory-strike' };
      },
    },
  };
}

export function generalKnightedRetinue(color: Color): RuleModifier {
  return {
    id: 'general-knighted-retinue',
    ownerSide: color,
    conflictTags: ['movement.all', 'promotion'],
    priority: 35,
    hooks: {
      onMoveResolve(move, state) {
        const p = state.pieces[move.pieceId];
        if (!p || p.color !== color) return [];
        if (move.promotionTo) p.customFlags['knightedRetinue'] = true;
        return [];
      },
      extendLegalMoves(piece, state) {
        if (piece.color !== color || !piece.customFlags['knightedRetinue']) return [];
        const out: Move[] = [];
        for (const [df, dr] of [
          [1, 2], [2, 1], [2, -1], [1, -2],
          [-1, -2], [-2, -1], [-2, 1], [-1, 2],
        ] as [number, number][]) {
          const t = offsetSquare(piece.square, df, dr);
          if (!t || friendlyAt(state, t, color)) continue;
          out.push(makeMove(piece, t, state));
        }
        return out;
      },
    },
  };
}

export function generalStoredTempo(color: Color): RuleModifier {
  return {
    id: 'general-stored-tempo',
    ownerSide: color,
    conflictTags: ['turn.bonus'],
    priority: 30,
    hooks: {
      // The Pass move is a UI-level concept: we treat it as a special move type
      // exposed by extendSpecialMoves. The bonus move grant happens in turnBonusGranted.
      extendSpecialMoves(state) {
        if (state.turn !== color) return [];
        // Pass turn → store 0.5 tempo. UI surfaces a button.
        return [];
      },
      extendSpecialActions(state) {
        if (state.turn !== color || state.pendingBonus) return [];
        return [{ kind: 'store_tempo', side: color }];
      },
      turnBonusGranted(move, state) {
        const tempo = (state.ruleState['stored-tempo-' + color] as number | undefined) ?? 0;
        if (tempo < 1) return null;
        const lastMover = state.pieces[move.pieceId];
        if (!lastMover) return null;
        const eligible = piecesOfColor(state, color).filter((p) => p.id !== lastMover.id).map((p) => p.id);
        if (eligible.length === 0) return null;
        state.ruleState['stored-tempo-' + color] = tempo - 1;
        return {
          side: color,
          kind: 'stored_tempo',
          canCapture: true,
          restrictToPieceIds: eligible,
        };
      },
    },
  };
}

export function generalCastleMaster(color: Color): RuleModifier {
  return {
    id: 'general-castle-master',
    ownerSide: color,
    conflictTags: ['special'],
    priority: 65,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'king') return [];
        const moves: Move[] = [];
        for (const partner of piecesOfColor(state, color)) {
          if (partner.id === piece.id || partner.type === 'king') continue;
          const df = Math.sign(fileIndex(fileOf(partner.square)) - fileIndex(fileOf(piece.square)));
          const dr = Math.sign(rankOf(partner.square) - rankOf(piece.square));
          if (df !== 0 && dr !== 0) continue;
          const distance = Math.max(
            Math.abs(fileIndex(fileOf(partner.square)) - fileIndex(fileOf(piece.square))),
            Math.abs(rankOf(partner.square) - rankOf(piece.square)),
          );
          if (distance < 3 || !pathBetweenClear(state, piece.square, partner.square, df, dr)) continue;
          const kingTo = offsetSquare(piece.square, df * 2, dr * 2);
          const partnerTo = offsetSquare(piece.square, df, dr);
          if (!kingTo || !partnerTo || !emptyAt(state, kingTo) || !emptyAt(state, partnerTo)) continue;
          moves.push({
            from: piece.square,
            to: kingTo,
            pieceId: piece.id,
            special: 'castle_master',
            ruleTriggers: [`castle-master:${partner.id}:${partnerTo}`],
          });
        }
        return moves;
      },
      onMoveResolve(move, state) {
        if (move.special !== 'castle_master') return [];
        const trigger = move.ruleTriggers?.find((t) => t.startsWith('castle-master:'));
        if (!trigger) return [];
        const [, partnerId, partnerTo] = trigger.split(':');
        const partner = partnerId ? state.pieces[partnerId] : null;
        if (partner && partner.color === color && partnerTo) {
          partner.square = partnerTo;
          partner.hasMoved = true;
        }
        return [];
      },
    },
  };
}

export function generalFlexibleDeployment(color: Color): RuleModifier {
  return {
    id: 'general-flexible-deployment',
    ownerSide: color,
    conflictTags: ['promotion'],
    priority: 50,
    hooks: {
      extendLegalMoves(piece, state) {
        if (piece.color !== color || piece.type !== 'pawn') return [];
        const fwd = color === 'white' ? 1 : -1;
        const promoteFromRank = color === 'white' ? 7 : 2;
        if (rankOf(piece.square) !== promoteFromRank) return [];
        const ahead = offsetSquare(piece.square, 0, fwd);
        if (!ahead) return [];
        const out: Move[] = [];
        if (emptyAt(state, ahead) || enemyAt(state, ahead, color)) {
          const capturedPieceId = enemyAt(state, ahead, color) ? pieceAt(state, ahead)?.id : undefined;
          for (const promotionTo of ['queen', 'rook', 'bishop', 'knight'] as const) {
            if (capturedPieceId) {
              out.push({
                from: piece.square,
                to: ahead,
                pieceId: piece.id,
                capturedPieceId,
                promotionTo,
                special: 'flexible_deployment',
              });
            }
            if (!capturedPieceId) {
              for (const to of backTwoRankSquares(state, color)) {
                out.push({
                  from: piece.square,
                  to,
                  pieceId: piece.id,
                  promotionTo,
                  special: 'flexible_deployment',
                  ruleTriggers: [`promotion-path:${ahead}`],
                });
              }
            }
          }
        }
        return out;
      },
    },
  };
}

export function generalRoyalEscort(color: Color): RuleModifier {
  return {
    id: 'general-royal-escort',
    ownerSide: color,
    conflictTags: ['turn.bonus', 'movement.king'],
    priority: 40,
    hooks: {
      turnBonusGranted(move, state) {
        const p = state.pieces[move.pieceId];
        if (!p || p.color !== color || p.type !== 'king') return null;
        if (move.bonusOf !== undefined) return null;
        // Find currently-threatened friendly pieces; they may move 1 square.
        const enemy = color === 'white' ? 'black' : 'white';
        const threatened: string[] = [];
        for (const f of piecesOfColor(state, color)) {
          if (f.id === p.id) continue;
          if (squareAttackedBy(state, f.square, enemy)) threatened.push(f.id);
        }
        if (threatened.length === 0) return null;
        return {
          side: color,
          kind: 'royal_escort',
          canCapture: true,
          restrictToPieceIds: threatened,
          maxDistance: 1,
        };
      },
    },
  };
}

/**
 * Conservative "does a piece of `attackerColor` attack `square`?" check.
 * Uses only base chess attack rules — does NOT consider card-extended movement.
 * Good enough for Momentum Check and Royal Escort's "threatened" detection.
 */
export function squareAttackedBy(state: GameState, square: Square, attackerColor: Color): boolean {
  // Pawns
  const pawnFwd = attackerColor === 'white' ? 1 : -1;
  for (const df of [-1, 1] as const) {
    const from = offsetSquare(square, df, -pawnFwd);
    if (from) {
      const occ = pieceAt(state, from);
      if (occ && occ.color === attackerColor && occ.type === 'pawn') return true;
    }
  }
  // Knights
  for (const [df, dr] of [
    [1, 2], [2, 1], [2, -1], [1, -2],
    [-1, -2], [-2, -1], [-2, 1], [-1, 2],
  ] as [number, number][]) {
    const from = offsetSquare(square, df, dr);
    if (!from) continue;
    const occ = pieceAt(state, from);
    if (occ && occ.color === attackerColor && occ.type === 'knight') return true;
  }
  // Sliding (rook/bishop/queen)
  for (const [df, dr] of [...ORTHO_OFFSETS] as [number, number][]) {
    let f = df, r = dr;
    while (true) {
      const t = offsetSquare(square, f, r);
      if (!t) break;
      const occ = pieceAt(state, t);
      if (!occ) { f += df; r += dr; continue; }
      if (occ.color === attackerColor && (occ.type === 'rook' || occ.type === 'queen')) return true;
      break;
    }
  }
  for (const [df, dr] of [...DIAG_OFFSETS] as [number, number][]) {
    let f = df, r = dr;
    while (true) {
      const t = offsetSquare(square, f, r);
      if (!t) break;
      const occ = pieceAt(state, t);
      if (!occ) { f += df; r += dr; continue; }
      if (occ.color === attackerColor && (occ.type === 'bishop' || occ.type === 'queen')) return true;
      break;
    }
  }
  // King adjacency
  for (const [df, dr] of [...ORTHO_OFFSETS, ...DIAG_OFFSETS] as [number, number][]) {
    const from = offsetSquare(square, df, dr);
    if (!from) continue;
    const occ = pieceAt(state, from);
    if (occ && occ.color === attackerColor && occ.type === 'king') return true;
  }
  return false;
}

function pathBetweenClear(state: GameState, from: Square, to: Square, df: number, dr: number): boolean {
  let step = 1;
  while (true) {
    const sq = offsetSquare(from, df * step, dr * step);
    if (!sq) return false;
    if (sq === to) return true;
    if (pieceAt(state, sq)) return false;
    step += 1;
  }
}

function backTwoRankSquares(state: GameState, color: Color): Square[] {
  const ranks = color === 'white' ? [1, 2] : [7, 8];
  const out: Square[] = [];
  for (const rank of ranks) {
    for (let file = 0; file < 8; file++) {
      const square = `${String.fromCharCode('a'.charCodeAt(0) + file)}${rank}` as Square;
      if (emptyAt(state, square)) out.push(square);
    }
  }
  return out;
}
